import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getStats } from '../../store/slices/adminSlice';
import {
  Typography, Row, Col, Spin,
  UserOutlined, DollarOutlined, TeamOutlined,
  CustomDatePicker, CustomSelect, Colors
} from '../../utils/commonImports';
import dayjs from 'dayjs';

const { Text } = Typography;

const Home = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.admin);
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'week'));
  const [endDate, setEndDate] = useState(dayjs());
  const [selectedRange, setSelectedRange] = useState(6);

  const rangeOptions = [
    { label: 'Last 1 Day', value: 0 },
    { label: 'Last 5 Days', value: 4 },
    { label: 'Last 7 Days', value: 6 },
    { label: 'Last 30 Days', value: 29 },
    { label: 'Last 6 months', value: 179 }
  ];

  const handleRangeChange = (value) => {
    setSelectedRange(value);
    const newEndDate = dayjs();
    const newStartDate = dayjs().subtract(value, 'day');
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  useEffect(() => {
    dispatch(getStats({
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD')
    }));
  }, [dispatch, startDate, endDate]);

  const MetricCard = ({ title, value, icon, color, pattern = 'default' }) => {
    const getBackgroundPattern = () => {
      switch(pattern) {
        case 'circles':
          return (
            <>
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: `${color}15`,
                border: `2px solid ${color}25`
              }} />
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '30px',
                height: '30px',
                borderRadius: '50%',
                background: `${color}20`
              }} />
            </>
          )
        case 'waves':
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '60px',
              background: `linear-gradient(45deg, ${color}15 25%, transparent 25%), linear-gradient(-45deg, ${color}15 25%, transparent 25%)`,
              backgroundSize: '20px 20px'
            }} />
          )
        case 'diagonal':
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '0',
              height: '0',
              borderLeft: '80px solid transparent',
              borderTop: `80px solid ${color}20`
            }} />
          )
        case 'dots':
          return (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              width: '60px',
              height: '60px',
              background: `radial-gradient(circle, ${color}25 2px, transparent 2px)`,
              backgroundSize: '12px 12px'
            }} />
          )
        default:
          return (
            <div style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '80px',
              height: '80px',
              background: `linear-gradient(135deg, ${color}20, ${color}10)`,
              borderRadius: '0 16px 0 80px'
            }} />
          )
      }
    }
    
    return (
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: `1px solid ${Colors.lightGray}`,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)'
        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.12)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0px)'
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        {getBackgroundPattern()}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '24px',
            flexShrink: 0
          }}>
            {icon}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '4px' }}>
              <Text style={{ color: Colors.gray, fontSize: '14px', fontWeight: '500' }}>{title}</Text>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#262626', lineHeight: 1 }}>
              {value || 0}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Helper functions to get data from API response
  const getTotalUsers = () => data?.users?.total || 0;
  const getAdmins = () => data?.users?.byRole?.find(role => role._id === 'admin')?.count || 0;
  const getUsers = () => data?.users?.byRole?.find(role => role._id === 'user')?.count || 0;
  
  const getCorporateClientData = () => data?.transactions?.byCategory?.find(cat => cat._id.accountType === 'corporate' && cat._id.status === 'client') || {};
  const getCorporateInhouseData = () => data?.transactions?.byCategory?.find(cat => cat._id.accountType === 'corporate' && cat._id.status === 'inhouse') || {};
  const getEscrowClientData = () => data?.transactions?.byCategory?.find(cat => cat._id.accountType === 'escrow' && cat._id.status === 'client') || {};
  const getEscrowInhouseData = () => data?.transactions?.byCategory?.find(cat => cat._id.accountType === 'escrow' && cat._id.status === 'inhouse') || {};
  
  // Installment helper functions
  const getCorporateClientInstallments = () => data?.installments?.byCategory?.find(cat => cat._id.accountType === 'corporate' && cat._id.status === 'client') || {};
  const getCorporateInhouseInstallments = () => data?.installments?.byCategory?.find(cat => cat._id.accountType === 'corporate' && cat._id.status === 'inhouse') || {};
  const getEscrowClientInstallments = () => data?.installments?.byCategory?.find(cat => cat._id.accountType === 'escrow' && cat._id.status === 'client') || {};
  const getEscrowInhouseInstallments = () => data?.installments?.byCategory?.find(cat => cat._id.accountType === 'escrow' && cat._id.status === 'inhouse') || {};
  
  // Note: Home page uses 'status' from API response which is mapped from 'businessStatus' in aggregation

  return (
    <Spin spinning={loading}>
      <div>
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Date Range</Text>
            </div>
            <CustomSelect
              value={selectedRange}
              onChange={handleRangeChange}
              options={rangeOptions}
              placeholder="Select range"
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Start Date</Text>
            </div>
            <CustomDatePicker
              value={startDate}
              onChange={setStartDate}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>End Date</Text>
            </div>
            <CustomDatePicker
              value={endDate}
              onChange={setEndDate}
            />
          </Col>
        </Row>
        
        <div style={{ marginTop: '32px', marginBottom: '16px' }}>
          <Text style={{ fontSize: '20px', fontWeight: '600', color: Colors.primary }}>Transactions</Text>
        </div>
        
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Corporate Client (${getCorporateClientData().count || 0})`}
              value={`₹${(getCorporateClientData().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.success}
              pattern="dots"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Corporate Inhouse (${getCorporateInhouseData().count || 0})`}
              value={`₹${(getCorporateInhouseData().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.warning}
              pattern="circles"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Escrow Client (${getEscrowClientData().count || 0})`}
              value={`₹${(getEscrowClientData().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.darkGray}
              pattern="waves"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Escrow Inhouse (${getEscrowInhouseData().count || 0})`}
              value={`₹${(getEscrowInhouseData().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.gray}
              pattern="diagonal"
            />
          </Col>
        </Row>
        
        <div style={{ marginTop: '32px', marginBottom: '16px' }}>
          <Text style={{ fontSize: '20px', fontWeight: '600', color: Colors.primary }}>Installments</Text>
        </div>
        
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Corporate Client (${getCorporateClientInstallments().count || 0})`}
              value={`₹${(getCorporateClientInstallments().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.success}
              pattern="waves"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Corporate Inhouse (${getCorporateInhouseInstallments().count || 0})`}
              value={`₹${(getCorporateInhouseInstallments().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.warning}
              pattern="dots"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Escrow Client (${getEscrowClientInstallments().count || 0})`}
              value={`₹${(getEscrowClientInstallments().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.darkGray}
              pattern="diagonal"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title={`Escrow Inhouse (${getEscrowInhouseInstallments().count || 0})`}
              value={`₹${(getEscrowInhouseInstallments().totalAmount || 0).toFixed(2)}`}
              icon={<DollarOutlined />}
              color={Colors.gray}
              pattern="circles"
            />
          </Col>
        </Row>

        <div style={{ marginTop: '32px', marginBottom: '16px' }}>
          <Text style={{ fontSize: '20px', fontWeight: '600', color: Colors.primary }}>Users</Text>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title="Total"
              value={getTotalUsers()}
              icon={<TeamOutlined />}
              color={Colors.primary}
              pattern="circles"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title="Admins"
              value={getAdmins()}
              icon={<UserOutlined />}
              color={Colors.error}
              pattern="waves"
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <MetricCard
              title="Users"
              value={getUsers()}
              icon={<UserOutlined />}
              color={Colors.info}
              pattern="diagonal"
            />
          </Col>
        </Row>
      </div>
    </Spin>
  );
};

export default Home;